import {
  BusinessManagerGroup,
  BusinessManagerStage,
  FunnelTemplate,
  funnelTemplatePresetID,
  isPerBusinessSource,
} from '@marketing-milk/interfaces'
import Preset from 'app/interface/data/funnel/preset'
import Source from 'app/interface/data/funnel/source'

export { isPerBusinessSource }

/**
 * A saved template, shaped for the existing preset picker.
 *
 * Mapped rather than spread. `FunnelTemplate` lines up with `Preset` on every
 * top-level field, but its stages come from `FunnelStageConfigDTO`, which types
 * `est_conversion_name` as `string | null` where `BusinessManagerStage` wants a
 * plain `string` — so `{ ...template }` does not compile under admin's `strict`.
 * Coercing the null to `''` here is honest about it: `''` is exactly what the
 * built-in "Custom" preset already ships for that field. Casting instead would
 * hand `setPreset` a null it is not typed to expect.
 */
export function presetFromTemplate(template: FunnelTemplate): Preset {
  return {
    id: funnelTemplatePresetID(template.id),
    name: template.name,
    desc: template.desc,
    icon: template.icon,
    sourceNames: template.sourceNames ?? {},
    stages: template.stages.map(stage => ({
      ...stage,
      est_conversion_name: stage.est_conversion_name ?? '',
      data_sources_id: stage.data_sources_id ?? undefined,
      campaign_goal: stage.campaign_goal ?? undefined,
      stage_target: stage.stage_target ?? undefined,
    })) as BusinessManagerStage[],
  }
}

export type PerBusinessSourceKind = 'custom_conversion' | 'insight'

export const perBusinessSourceKind = (key: string): PerBusinessSourceKind =>
  key.startsWith('custom_conversions/') ? 'custom_conversion' : 'insight'

/** A per-business source the target business can't be shown to have. */
export interface UnverifiedSource {
  stageIndex: number
  stageName: string
  groupIndex: number
  groupName: string
  key: string
  /** The name stored with the template, or the raw key if none was. */
  name: string
  kind: PerBusinessSourceKind
  /** A custom conversion on the target business with exactly the same name. */
  replacement?: { key: string; name: string }
}

/**
 * What the editor remembers about an unverified source from a template: either
 * removed, or kept because the user said it's valid here.
 */
export interface FlaggedSource {
  key: string
  name: string
  kind: PerBusinessSourceKind
  kept: boolean
}

/**
 * Editor-only marker on a group, cleared when the user edits that group's
 * sources. It must never be sent anywhere: see withoutTemplateMarkers.
 */
export type MarkedGroup = BusinessManagerGroup & { flagged_sources?: FlaggedSource[] }

export const flaggedSourcesOf = (group: BusinessManagerGroup): FlaggedSource[] =>
  (group as MarkedGroup).flagged_sources ?? []

/**
 * The keys ApplyTemplateDialog starts with "Keep anyway" checked. On the create
 * page nothing can be confirmed, and a template is usually applied within its
 * own account family, so everything is kept. On an existing business only
 * insights are: their keys are action type names, so they usually carry over,
 * while a custom conversion id belongs to one ad account.
 */
export const defaultKeeps = (unverified: UnverifiedSource[], businessKnown: boolean): string[] =>
  Array.from(
    new Set(
      unverified.filter(item => !businessKnown || item.kind === 'insight').map(item => item.key)
    )
  )

const normalizeName = (name: string) => name.trim().toLowerCase()

/**
 * Every per-business source in `preset` that `sources` can't confirm exists on
 * the target business.
 *
 * Only a list described *for this business* can confirm anything. create.tsx
 * loads the global list (`businessID: null`), so there every per-business
 * source is unverified. Even a business's own list can lag: ingest rebuilds its
 * business -> private-key map every 60–90 minutes, so a just-linked ad account
 * or just-added custom conversion may be missing. That is why this never
 * decides on its own — the caller shows the result and the user confirms.
 */
export function findUnverifiedSources(
  preset: Pick<Preset, 'stages' | 'sourceNames'>,
  sources: { data: Source[]; businessID: number | null }
): UnverifiedSource[] {
  const targetSources = sources.businessID === null ? [] : sources.data
  const available = new Set(targetSources.map(source => source.meta.key))
  const conversionsByName = new Map<string, Source[]>()
  for (const source of targetSources) {
    if (!source.meta.key.startsWith('custom_conversions/')) continue
    const name = normalizeName(source.meta.name ?? '')
    conversionsByName.set(name, [...(conversionsByName.get(name) ?? []), source])
  }

  const unverified: UnverifiedSource[] = []
  preset.stages.forEach((stage, stageIndex) => {
    ;(stage.groups ?? []).forEach((group, groupIndex) => {
      for (const key of group.sources ?? []) {
        if (!isPerBusinessSource(key) || available.has(key)) continue

        const storedName = preset.sourceNames?.[key]
        const kind = perBusinessSourceKind(key)
        // Offered only for an unambiguous match on a name we actually stored:
        // two same-named conversions, or a template saved before names were
        // kept, leave the choice to the user.
        const matches = storedName ? conversionsByName.get(normalizeName(storedName)) ?? [] : []
        const replacement =
          kind === 'custom_conversion' && matches.length === 1
            ? { key: matches[0].meta.key, name: matches[0].meta.name }
            : undefined

        unverified.push({
          stageIndex,
          stageName: stage.name,
          groupIndex,
          groupName: group.name,
          key,
          name: storedName ?? key,
          kind,
          replacement,
        })
      }
    })
  })
  return unverified
}

/**
 * Swaps each unverified source for its replacement when that replacement's key
 * is in `acceptedReplacements`, leaves it in place when its key is in
 * `acceptedKeeps`, and otherwise removes it. Every source not replaced is
 * flagged on its group so the editor can keep saying so.
 *
 * Mutates `stages` in place because the caller has already deep-cloned them.
 */
export function applyUnverifiedSources(
  stages: BusinessManagerStage[],
  unverified: UnverifiedSource[],
  acceptedReplacements: ReadonlySet<string>,
  acceptedKeeps: ReadonlySet<string> = new Set()
): void {
  for (const item of unverified) {
    const group = stages[item.stageIndex]?.groups?.[item.groupIndex] as MarkedGroup | undefined
    if (!group) continue

    const replacement =
      item.replacement && acceptedReplacements.has(item.replacement.key)
        ? item.replacement.key
        : null
    if (replacement) {
      const sources = group.sources.map(key => (key === item.key ? replacement : key))
      group.sources = Array.from(new Set(sources))
      continue
    }

    const kept = acceptedKeeps.has(item.key)
    if (!kept) group.sources = group.sources.filter(key => key !== item.key)
    group.flagged_sources = [
      ...(group.flagged_sources ?? []),
      { key: item.key, name: item.name, kind: item.kind, kept },
    ]
  }
}

/** Groups that lost a source to a template and now have none at all. */
export function emptiedGroups(
  stages: BusinessManagerStage[]
): { stageName: string; groupName: string }[] {
  return stages.flatMap(stage =>
    (stage.groups ?? [])
      .filter(group => flaggedSourcesOf(group).length > 0 && (group.sources ?? []).length === 0)
      .map(group => ({ stageName: stage.name, groupName: group.name }))
  )
}

/**
 * The business with the editor-only group markers taken off, for sending. The
 * business endpoints take the raw body, so a marker left on would reach the
 * upsert code.
 */
export function withoutTemplateMarkers<T extends { funnel: { stages: BusinessManagerStage[] } }>(
  business: T
): T {
  return {
    ...business,
    funnel: {
      ...business.funnel,
      stages: business.funnel.stages.map(stage => ({
        ...stage,
        groups: (stage.groups ?? []).map(group => {
          const { flagged_sources, ...rest } = group as MarkedGroup
          return rest
        }),
      })),
    },
  }
}
